import {
  createUniqueId,
  For,
  createMemo,
  Match,
  Show,
  Switch,
  type ComponentProps,
  type ParentProps,
  type JSX,
  onMount,
  type Ref,
  onCleanup,
  createSignal,
  createEffect,
  type Accessor,
  splitProps,
} from "solid-js";
import { Portal } from "solid-js/web";
import { Icon } from "~/icon";
import { getSearchParams, router, usePage, type Page } from "~/inertia";
import { applyMulti, normalizeMulti } from "~/common/report_params";
import { formatDate } from "~/util/format";
import { createFloating } from "~/util/html/floating";
import clsx from "~/util/html/style";
import { Button, IconButton } from "./button";
import { Toggle } from "./toggle";

export type FilterButtonWidth = "md" | "fit";

// A native modal <dialog> renders in the browser's top layer, above anything
// portaled to <body>. So when a filter button lives inside an open modal, mount its
// popover into that dialog (also top layer) instead of <body>, otherwise the popover
// opens behind the modal and looks like nothing happens. Mirrors resolveAutoMount in
// ~/common/floating. Returns undefined off-modal so Portal defaults to <body>.
function closestOpenDialog(element: HTMLElement | undefined): HTMLElement | undefined {
  const dialog = element?.closest("dialog[open]");
  return dialog instanceof HTMLElement ? dialog : undefined;
}

/** A data table's row link is a full-bleed transparent overlay, so table space that
 * looks blank is a click target. See DataTableRowButton / DataTableRowA. */
export function isRowLink(target: Node) {
  return target instanceof Element && !!target.closest("[data-row-link]");
}

/** Geometry for anything that sits in a card header's filter cluster. */
export const FILTER_PILL_SHAPE_CLASSES =
  "flex h-9 items-center gap-2 rounded-full whitespace-nowrap";

export function FilterButton(
  props: ParentProps<{
    onClick?: () => void;
    class?: string;
    ref?: Ref<HTMLButtonElement>;
    width?: FilterButtonWidth;
  }>,
) {
  const [local, buttonProps] = splitProps(props, ["class", "width"]);
  const width = () => local.width ?? "md";

  return (
    <button
      {...buttonProps}
      class={clsx(
        FILTER_PILL_SHAPE_CLASSES,
        "group/date-filter relative cursor-pointer truncate bg-brand-light px-4 hover:brightness-98",
        width() === "md" && "max-w-56",
        width() === "fit" && "w-fit",
        local.class,
      )}
    />
  );
}

function RemovableFilterPill(props: { label: string; onRemove: () => void }) {
  return (
    <span class={clsx(FILTER_PILL_SHAPE_CLASSES, "bg-brand-light pr-2 pl-4")}>
      {props.label}
      <IconButton
        variant="plain"
        size="xs"
        iconName="x"
        aria-label={`Remove ${props.label} filter`}
        onClick={props.onRemove}
      />
    </span>
  );
}

export function QueryParamRemovableFilterPill(props: {
  queryParam: string;
  label: string;
  reloadProp?: string | false | string[];
}) {
  const page = usePage();

  return (
    <Show when={getSearchParams(page).get(props.queryParam)}>
      <RemovableFilterPill
        label={props.label}
        onRemove={() => setQueryParamFilter(props.queryParam, undefined, props.reloadProp)}
      />
    </Show>
  );
}

export function FilterButtonContent(props: {
  for: string;
  label: string;
  value: string | undefined;
}) {
  return (
    <>
      <label
        class="pointer-events-none m-0 text-typography-secondary group-hover/date-filter:text-typography-primary"
        for={props.for}
      >
        {props.label}
        <Show when={props.value}>:</Show>
      </label>

      <Show when={props.value}>
        <span class="min-w-0 truncate text-sm text-typography-primary">{props.value}</span>
      </Show>

      <Icon
        name="chevronDown"
        size="sm"
        class="ml-auto text-typography-secondary group-hover/date-filter:text-typography-primary"
      />
    </>
  );
}

export function DateFilterButton(props: {
  label: string;
  value: string | undefined;
  onChange: (value: string) => void;
  displayValue: string | undefined;
  min?: string;
  max?: string;
  width?: FilterButtonWidth;
}) {
  const id = createUniqueId();
  let inputRef!: HTMLInputElement;

  return (
    <FilterButton onClick={() => inputRef.showPicker()} width={props.width}>
      <input
        ref={inputRef}
        id={id}
        type="date"
        value={props.value}
        min={props.min}
        max={props.max}
        onChange={(event) => {
          props.onChange(event.target.value);
        }}
        class="pointer-events-none absolute right-0 bottom-0 left-0 h-0 border-none p-0 focus:outline-none"
        tabIndex={-1}
      />

      <FilterButtonContent for={id} label={props.label} value={props.displayValue} />
    </FilterButton>
  );
}

export function QueryParamDateFilterButton(props: {
  queryParam: string;
  label: string;
  reloadProp?: string | string[];
  showProgress?: boolean;
}) {
  const page = usePage();

  const value = () => {
    return getSearchParams(page).get(props.queryParam) ?? undefined;
  };
  const formattedValue = () => {
    const v = value();
    return v ? formatDate(v) : undefined;
  };

  return (
    <DateFilterButton
      label={props.label}
      value={value()}
      onChange={(dateString) => {
        const reloadProp = Array.isArray(props.reloadProp)
          ? props.reloadProp
          : props.reloadProp
            ? [props.reloadProp]
            : undefined;
        router.reload({
          data: {
            [props.queryParam]: dateString,
          },
          replace: true,
          showProgress: props.showProgress,
          ...(reloadProp ? { only: reloadProp } : {}),
        });
      }}
      displayValue={formattedValue()}
    />
  );
}

/** The bucket a leaf option falls into when a `groupBy` control is switched on. */
export type SelectOptionGroup = { id: string; label: string };

export type SelectOption = {
  label: string;
  /** use hidden instead of pre-filtering options when passing in, this way options won't default to unselected when options are made visible and selections will persist across filter visibility changes */
  hidden?: boolean;
  /** Extra text the search box matches, alongside the label — an alternate name, a code. */
  searchKeywords?: string[];
} & (
  | {
      value: string;
      /** Secondary text on the option's row (a count, a status). Never part of the label,
       * so the selected-value text on the button stays the label alone. */
      detail?: string;
      group?: SelectOptionGroup;
    }
  | { options: SelectOption[] }
);

/** A pinned toggle above the dropdown's option list that buckets the flat list by each
 * option's `group`. The caller owns the state, so it can persist the choice. */
export type SelectFilterGroupBy = {
  label: string;
  enabled: boolean;
  onChange: (enabled: boolean) => void;
};

type SelectFilterHeaderScope = {
  isSearching: boolean;
  matchingValues: string[];
};

/** Maps server-serialized `{id, name}` records to filter select options. */
export function toFilterOptions(options: { id: string; name: string }[]): SelectOption[] {
  return options.map((option) => ({ label: option.name, value: option.id }));
}

// TODO: make a lower level Popover render prop component like Tooltip
function BaseSelectFilterButton(props: {
  class?: string;
  label: string;
  options: SelectOption[];
  selectedValues: string[];
  displayValue: string | undefined;
  onOptionChange: (value: string, isChecked: boolean) => void;
  onBatchChange?: (items: { value: string; isChecked: boolean }[]) => void;
  onSelectOnly?: (value: string) => void;
  onClose?: () => void;
  multiple: boolean;
  header?: (scope: Accessor<SelectFilterHeaderScope>) => JSX.Element;
  groupBy?: SelectFilterGroupBy;
  emptyContent?: JSX.Element;
  width?: FilterButtonWidth;
  stackedDetails?: boolean;
}) {
  const id = createUniqueId();

  const [isOpen, setIsOpen] = createSignal(false);
  const [searchQuery, setSearchQuery] = createSignal("");

  let trigger!: HTMLButtonElement;
  let popover!: HTMLFieldSetElement;
  let searchInput: HTMLInputElement | undefined;
  let cleanup: (() => void) | null = null;

  createEffect(() => {
    if (isOpen()) {
      cleanup = createFloating({
        reference: trigger,
        floating: popover,
        config: {
          placement: "bottom-end",
        },
      });
      searchInput?.focus();
    } else if (cleanup) {
      cleanup();
      cleanup = null;
      popover.style.width = "";
      setSearchQuery("");
      props.onClose?.();
    }
  });

  onCleanup(() => {
    if (cleanup) {
      cleanup();
    }
  });

  onMount(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;

      if (trigger.contains(target) || popover.contains(target)) {
        return;
      }

      if (!isOpen()) {
        return;
      }

      setIsOpen(false);
      if (isRowLink(target)) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    document.addEventListener("click", handleClickOutside, true);

    onCleanup(() => {
      document.removeEventListener("click", handleClickOutside, true);
    });
  });

  const groupedOptions = createMemo(() =>
    props.groupBy?.enabled ? groupSelectOptions(props.options) : props.options,
  );

  const visibleOptions = createMemo(() =>
    groupedOptions().filter((option) => hasVisibleLeafOptions(option)),
  );

  const showSearch = createMemo(
    () =>
      getMultiSelectValueOptions(props.options).filter((option) => !option.hidden).length >
      SEARCH_OPTION_THRESHOLD,
  );

  const filteredOptions = createMemo(() =>
    showSearch() ? filterOptionsByQuery(visibleOptions(), searchQuery()) : visibleOptions(),
  );

  const headerScope = createMemo(() => ({
    isSearching: showSearch() && searchQuery().trim().length > 0,
    matchingValues: getMultiSelectValueOptions(filteredOptions())
      .filter((option) => !option.hidden)
      .map((option) => option.value),
  }));

  return (
    <>
      <FilterButton
        class={props.class}
        ref={trigger}
        width={props.width}
        onClick={() => setIsOpen((isOpen) => !isOpen)}
      >
        <FilterButtonContent for={id} label={props.label} value={props.displayValue} />
      </FilterButton>

      <Portal mount={closestOpenDialog(trigger)}>
        <fieldset
          id={id}
          ref={popover}
          class={clsx(
            "fixed z-floating-attachment max-h-[min(32rem,var(--floating-available-height,32rem))] cursor-default flex-col overflow-hidden rounded-xl bg-white text-left shadow-md-all",
            isOpen() ? "flex" : "hidden",
          )}
        >
          <Show when={showSearch()}>
            <div class="flex shrink-0 items-center gap-2 border-b border-brand-light px-4 py-2">
              <Icon name="search" class="size-4 shrink-0 text-typography-secondary" />
              <input
                ref={searchInput}
                type="text"
                value={searchQuery()}
                onInput={(event) => {
                  // Pin the full-list width before narrowing it, so the popover doesn't
                  // resize to the widest remaining match on every keystroke.
                  if (!event.currentTarget.value) {
                    popover.style.width = "";
                  } else if (!popover.style.width) {
                    popover.style.width = `${popover.getBoundingClientRect().width}px`;
                  }
                  setSearchQuery(event.currentTarget.value);
                }}
                placeholder="Search"
                class="w-full border-0 bg-transparent p-0 text-sm text-typography-primary placeholder:text-typography-secondary focus:ring-0"
              />
            </div>
          </Show>

          {/* Under the search box, which is what every open reaches for first: the toggle is a
              sticky setting, and here it reads as a control over the list below it. */}
          <Show when={props.groupBy}>
            {(groupBy) => (
              <div class="shrink-0 border-b border-brand-light px-4 py-2">
                <Toggle
                  size="small"
                  label={groupBy().label}
                  checked={groupBy().enabled}
                  onChange={(event) => groupBy().onChange(event.currentTarget.checked)}
                />
              </div>
            )}
          </Show>

          <Show when={filteredOptions().length > 0 && props.header}>
            {(header) => <div class="my-3 flex shrink-0 gap-3 px-4">{header()(headerScope)}</div>}
          </Show>

          <div class="min-h-0 flex-1 overflow-y-auto">
            <For each={filteredOptions()}>
              {(option) => (
                <SelectFilterButtonOption
                  option={option}
                  selectedValues={props.selectedValues}
                  multiple={props.multiple}
                  onChange={props.onOptionChange}
                  onBatchChange={props.onBatchChange}
                  onSelectOnly={props.onSelectOnly}
                  depth={1}
                  closePopover={() => setIsOpen(false)}
                  stackedDetails={props.stackedDetails}
                />
              )}
            </For>

            <Show when={filteredOptions().length === 0}>
              <div class="px-4 py-2 text-sm text-typography-secondary">
                <Show when={showSearch() && searchQuery().trim()} fallback={props.emptyContent}>
                  No matches
                </Show>
              </div>
            </Show>
          </div>
        </fieldset>
      </Portal>
    </>
  );
}

export function SelectFilterButton(props: {
  label: string;
  options: SelectOption[];
  value: string | undefined;
  onChange: (value: string) => void;
  width?: FilterButtonWidth;
  // Sentence-length option details read badly right-aligned beside the label;
  // this stacks each one under its option instead.
  stackedDetails?: boolean;
}) {
  const selectedValues = () => (props.value !== undefined ? [props.value] : []);
  const displayValue = () => {
    if (props.value === undefined) return undefined;
    const option = findOptionByValue(props.options, props.value);
    return option?.label;
  };

  return (
    <BaseSelectFilterButton
      label={props.label}
      options={props.options}
      selectedValues={selectedValues()}
      displayValue={displayValue()}
      multiple={false}
      width={props.width}
      stackedDetails={props.stackedDetails}
      onOptionChange={(value) => props.onChange(value)}
    />
  );
}

export function MultiSelectFilterButton(props: {
  class?: string;
  label: string;
  options: SelectOption[];
  value: string[];
  bulkSelectors?: { label: string; values: { value: string; isChecked: boolean }[] }[];
  groupBy?: SelectFilterGroupBy;
  onChange: (value: string[]) => void;
  /** Fire onChange once on close, for filters where each tick drives expensive work. */
  commitOnClose?: boolean;
  emptyContent?: JSX.Element;
  width?: FilterButtonWidth;
}) {
  const [draft, setDraft] = createSignal<string[] | null>(null);
  const effectiveValue = () => draft() ?? props.value;

  function apply(next: string[]) {
    if (props.commitOnClose) {
      setDraft(() => next);
    } else {
      props.onChange(next);
    }
  }

  function commit() {
    const pending = draft();
    setDraft(null);
    if (pending) props.onChange(pending);
  }

  const allValueOptions = createMemo(() => getMultiSelectValueOptions(props.options));

  const visibleValueOptions = createMemo(() =>
    allValueOptions().filter((valueOption) => !valueOption.hidden),
  );
  const visibleSelectedOptions = createMemo(() => {
    const selected = effectiveValue();
    return visibleValueOptions().filter((valueOption) => selected.includes(valueOption.value));
  });

  const displayValue = () => {
    const selected = visibleSelectedOptions();
    if (selected.length === 0) return "None";
    if (selected.length === 1) {
      const option = findOptionByValue(props.options, selected[0]!.value);
      return option?.label;
    }
    const visibleOptions = visibleValueOptions();
    if (selected.length === visibleOptions.length) {
      return "All";
    }
    // TODO: could show smarter stuff like if all sub options of one "head" are selected, could show the head
    return `${selected.length} selected`;
  };

  function handleChange(value: string, isChecked: boolean) {
    const currentValue = effectiveValue();
    if (isChecked) {
      apply([...currentValue, value]);
    } else {
      apply(currentValue.filter((v) => v !== value));
    }
  }

  function handleBatchChange(items: { value: string; isChecked: boolean }[]) {
    const currentValue = effectiveValue();
    let newValues = [...currentValue];

    items.forEach(({ value, isChecked }) => {
      if (isChecked) {
        if (!newValues.includes(value)) {
          newValues.push(value);
        }
      } else {
        newValues = newValues.filter((v) => v !== value);
      }
    });

    apply(newValues);
  }

  return (
    <BaseSelectFilterButton
      class={props.class}
      label={props.label}
      options={props.options}
      selectedValues={effectiveValue()}
      displayValue={displayValue()}
      multiple
      onOptionChange={handleChange}
      onBatchChange={handleBatchChange}
      onSelectOnly={(value) => apply([value])}
      onClose={commit}
      width={props.width}
      groupBy={props.groupBy}
      header={
        allValueOptions().filter((valueOption) => !valueOption.hidden).length > 0
          ? (scope) => (
              <Show
                when={scope().isSearching}
                fallback={
                  <>
                    <For each={props.bulkSelectors}>
                      {(bulkSelector) => (
                        <SelectFilterBulkSelectButton
                          onClick={() => handleBatchChange(bulkSelector.values)}
                        >
                          {bulkSelector.label}
                        </SelectFilterBulkSelectButton>
                      )}
                    </For>
                    <SelectFilterButtonOptionHead
                      onBatchChange={handleBatchChange}
                      allValues={allValueOptions().map((valueOption) => valueOption.value)}
                      allSelected={allValueOptions().every((valueOption) =>
                        effectiveValue().includes(valueOption.value),
                      )}
                    />
                  </>
                }
              >
                <SelectFilterBulkSelectButton onClick={() => apply(scope().matchingValues)}>
                  Select matching
                </SelectFilterBulkSelectButton>
              </Show>
            )
          : undefined
      }
      emptyContent={props.emptyContent}
    />
  );
}

function findOptionByValue(options: SelectOption[], value: string): SelectOption | undefined {
  for (const option of options) {
    if ("value" in option && option.value === value) {
      return option;
    }
    if ("options" in option) {
      const found = findOptionByValue(option.options, value);
      if (found) return found;
    }
  }
  return undefined;
}

export function useMultiSelectFilterButtonValue(
  page: Page,
  options: Accessor<SelectOption[]>,
  queryParam: Accessor<`${string}[]`>,
) {
  const allValues = createMemo(() =>
    getMultiSelectValueOptions(options()).map((valueOption) => valueOption.value),
  );

  const signal = createMemo(() => {
    const bracketless = getQueryParamWithoutBrackets(queryParam());
    const normalized = normalizeMulti(getSearchParams(page), bracketless);
    if (normalized === null) return allValues();
    return normalized;
  });
  return signal;
}

/** What a multi-select's selection is worth as a query param: nothing selected
 * is the explicit empty scope, everything selected is no filter at all. Pages
 * that own several filter params in one write encode through here too, so the
 * sentinel and the drop-when-all rule can't drift between writers. */
export function multiSelectFilterValue(
  value: string[],
  allValues: string[],
): string | string[] | undefined {
  if (value.length === 0) return FILTER_NONE_VALUE;
  if (value.length === allValues.length) return undefined;
  return value;
}

export function QueryParamMultiSelectFilterButton(props: {
  class?: string;
  queryParam: `${string}[]`;
  label: string;
  options: SelectOption[];
  bulkSelectors?: { label: string; values: { value: string; isChecked: boolean }[] }[];
  reloadProp?: string | string[] | false;
  width?: FilterButtonWidth;
}) {
  const page = usePage();
  const allValues = createMemo(() =>
    getMultiSelectValueOptions(props.options).map((valueOption) => valueOption.value),
  );
  const value = useMultiSelectFilterButtonValue(
    page,
    () => props.options,
    () => props.queryParam,
  );

  return (
    <MultiSelectFilterButton
      class={props.class}
      label={props.label}
      options={props.options}
      value={value()}
      bulkSelectors={props.bulkSelectors}
      width={props.width}
      onChange={(value) => {
        handleFilterQueryParamChange(
          props.queryParam,
          multiSelectFilterValue(value, allValues()),
          props.reloadProp,
        );
      }}
    />
  );
}

export function getMultiSelectValueOptions(
  options: SelectOption[],
): (SelectOption & { value: string })[] {
  return flattenMultiSelectOptions(options).filter((option) => "value" in option);
}

export function flattenMultiSelectOptions(
  options: SelectOption[],
  allOptions: SelectOption[] = [],
  ancestorHidden: boolean = false,
): SelectOption[] {
  return options.flatMap((option) => {
    const hidden = option.hidden || ancestorHidden;
    if ("value" in option) {
      return [...allOptions, { ...option, hidden }];
    }
    return flattenMultiSelectOptions(
      option.options,
      [...allOptions, { ...option, hidden }],
      hidden,
    );
  });
}

function makeSelectFilterButtonOptionStyle(depth: number) {
  return {
    "padding-left": `calc(var(--spacing) * ${depth * 4})`,
  };
}

function hasVisibleLeafOptions(option: SelectOption): boolean {
  if ("value" in option) {
    return !option.hidden;
  }
  return option.options.some((childOption) => hasVisibleLeafOptions(childOption));
}

const SEARCH_OPTION_THRESHOLD = 10;

function matchesQuery(option: SelectOption, normalized: string): boolean {
  return [option.label, ...(option.searchKeywords ?? [])].some((text) =>
    text.toLowerCase().includes(normalized),
  );
}

function filterOptionsByQuery(options: SelectOption[], query: string): SelectOption[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return options;

  return options.flatMap((option): SelectOption[] => {
    if (option.hidden) return [];
    if ("value" in option) {
      return matchesQuery(option, normalized) ? [option] : [];
    }
    // Matching a group label keeps all of its children visible.
    if (matchesQuery(option, normalized)) {
      return [option];
    }
    const filteredChildren = filterOptionsByQuery(option.options, normalized);
    return filteredChildren.length > 0 ? [{ ...option, options: filteredChildren }] : [];
  });
}

/** Buckets a flat option list under each option's `group`, groups A→Z. Members keep the order
 * they were given in — whoever built the list owns that. Options with no group (and any list
 * that is already nested) are hoisted above the groups. */
function groupSelectOptions(options: SelectOption[]): SelectOption[] {
  const groups = new Map<string, { label: string; options: SelectOption[] }>();
  const ungrouped: SelectOption[] = [];

  for (const option of options) {
    const group = "value" in option ? option.group : undefined;
    if (!group) {
      ungrouped.push(option);
      continue;
    }
    const bucket = groups.get(group.id) ?? { label: group.label, options: [] };
    bucket.options.push(option);
    groups.set(group.id, bucket);
  }

  const grouped = [...groups.values()].sort((a, b) => a.label.localeCompare(b.label));

  return [...ungrouped, ...grouped];
}

function SelectFilterButtonOption(props: {
  option: SelectOption;
  selectedValues: string[];
  multiple: boolean;
  depth: number;
  onChange: (value: string, isChecked: boolean) => void;
  onBatchChange?: (items: { value: string; isChecked: boolean }[]) => void;
  onSelectOnly?: (value: string) => void;
  closePopover?: () => void;
  stackedDetails?: boolean;
}) {
  return (
    <Switch>
      <Match when={"value" in props.option && props.option}>
        {(option) => {
          const isSelected = () => props.selectedValues.includes(option().value);
          const effectiveDepth = Math.max(props.multiple ? props.depth - 1 : props.depth, 1);

          return (
            <label
              class="group/option-row m-0 flex cursor-pointer items-center gap-2 px-4 py-2 hover:bg-brand-hover"
              style={makeSelectFilterButtonOptionStyle(effectiveDepth)}
            >
              <input
                type={props.multiple ? "checkbox" : "radio"}
                value={option().value}
                checked={isSelected()}
                class={clsx("peer", props.multiple ? "form-checkbox" : "hidden")}
                onChange={(event) => {
                  if (props.multiple) {
                    props.onChange(event.target.value, event.target.checked);
                  } else {
                    props.onChange(event.target.value, true);
                    props.closePopover?.();
                  }
                }}
              />

              <Show
                when={props.stackedDetails}
                fallback={
                  <>
                    <span
                      class={clsx(
                        "mr-auto text-sm text-typography-primary",
                        !props.multiple && isSelected() && "cursor-default font-semibold",
                      )}
                    >
                      {option().label}
                    </span>

                    <Show when={option().detail}>
                      {(detail) => (
                        <span class="text-xs whitespace-nowrap text-typography-secondary">
                          {detail()}
                        </span>
                      )}
                    </Show>
                  </>
                }
              >
                <span class="mr-auto flex flex-col">
                  <span
                    class={clsx(
                      "text-sm text-typography-primary",
                      !props.multiple && isSelected() && "cursor-default font-semibold",
                    )}
                  >
                    {option().label}
                  </span>
                  <Show when={option().detail}>
                    {(detail) => <span class="text-xs text-typography-secondary">{detail()}</span>}
                  </Show>
                </span>
              </Show>

              <Show when={props.onSelectOnly}>
                {(onSelectOnly) => (
                  <SelectFilterBulkSelectButton
                    class="invisible group-hover/option-row:visible"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onSelectOnly()(option().value);
                    }}
                  >
                    Only
                  </SelectFilterBulkSelectButton>
                )}
              </Show>
            </label>
          );
        }}
      </Match>

      <Match when={"options" in props.option && props.option}>
        {(option) => {
          const allValues = createMemo(() =>
            getMultiSelectValueOptions(option().options).map((valueOption) => valueOption.value),
          );
          const allSelected = createMemo(() =>
            allValues().every((value) => props.selectedValues.includes(value)),
          );

          const hasVisibleOptions = createMemo(() => hasVisibleLeafOptions(option()));

          return (
            <Show when={hasVisibleOptions()}>
              <div
                class="group/select-filter-head flex items-center gap-3 px-4 py-2 text-sm text-typography-secondary"
                style={makeSelectFilterButtonOptionStyle(props.depth)}
              >
                <span>{option().label}</span>

                <Show when={props.onBatchChange}>
                  {(onBatchChange) => (
                    <SelectFilterButtonOptionHead
                      class="invisible group-hover/select-filter-head:visible"
                      onBatchChange={onBatchChange()}
                      allValues={allValues()}
                      allSelected={allSelected()}
                    />
                  )}
                </Show>
              </div>

              <For each={option().options.filter((option) => !option.hidden)}>
                {(nestedOption) => (
                  <SelectFilterButtonOption
                    option={nestedOption}
                    selectedValues={props.selectedValues}
                    multiple={props.multiple}
                    depth={props.depth + 1}
                    onChange={props.onChange}
                    onBatchChange={props.onBatchChange}
                    onSelectOnly={props.onSelectOnly}
                    closePopover={props.closePopover}
                    stackedDetails={props.stackedDetails}
                  />
                )}
              </For>
            </Show>
          );
        }}
      </Match>
    </Switch>
  );
}

function SelectFilterButtonOptionHead(props: {
  class?: string;
  onBatchChange: (items: { value: string; isChecked: boolean }[]) => void;
  allValues: string[];
  allSelected: boolean;
}) {
  return (
    <SelectFilterBulkSelectButton
      class={props.class}
      onClick={() =>
        props.onBatchChange(
          props.allValues.map((value) => ({ value, isChecked: !props.allSelected })),
        )
      }
    >
      <span class={clsx("absolute", props.allSelected && "invisible")}>Select all</span>
      <span class={props.allSelected ? undefined : "invisible"}>Select none</span>
    </SelectFilterBulkSelectButton>
  );
}

function SelectFilterBulkSelectButton(
  props: ParentProps<{
    class?: string;
    onClick: (e: MouseEvent) => void;
  }>,
) {
  return (
    <Button
      variant="plain-primary"
      size="xs"
      class={clsx("relative", props.class)}
      onClick={(e) => props.onClick(e)}
    >
      {props.children}
    </Button>
  );
}

type SelectFilterButtonProps = ComponentProps<typeof SelectFilterButton>;
export function QueryParamSelectFilterButton(props: {
  queryParam: string;
  label: SelectFilterButtonProps["label"];
  options: SelectFilterButtonProps["options"];
  /** Used when the query param is absent (e.g. initial load with server-side default). */
  defaultValue?: string;
  reloadProp?: string | false | string[];
  width?: SelectFilterButtonProps["width"];
}) {
  const page = usePage();

  const value = () => {
    const fromUrl = getSearchParams(page).get(props.queryParam);
    return fromUrl ?? props.defaultValue ?? "";
  };

  return (
    <SelectFilterButton
      label={props.label}
      options={props.options}
      onChange={(value) => {
        handleFilterQueryParamChange(props.queryParam, value, props.reloadProp);
      }}
      value={value()}
      width={props.width}
    />
  );
}

// the public write path for query-param filters — pages that need to set or clear a filter
// programmatically go through here instead of re-implementing the replace/preserve semantics
export function setQueryParamFilter(
  queryParam: string,
  value: string | string[] | undefined,
  reloadProp?: string | false | string[],
) {
  handleFilterQueryParamChange(queryParam, value, reloadProp);
}

/** Same write path for filters that move together — one navigation, one reload,
 * so a dependent filter can never be read at a scope its sibling just left. */
export function setQueryParamFilters(
  changes: Record<string, string | string[] | undefined>,
  reloadProp?: string | false | string[],
) {
  visitFilterUrl(applyFiltersToUrl(window.location.href, changes), reloadProp);
}

function handleFilterQueryParamChange(
  queryParam: string,
  value: string | string[] | undefined,
  reloadProp?: string | false | string[], // false means no reload, empty means reload all props. for back compat, prolly should change to true or "all" meaning reload all props or something
) {
  visitFilterUrl(applyFilterToUrl(window.location.href, queryParam, value), reloadProp);
}

// callers build the URL by hand instead of going through router.reload({data}). Inertia's
// mergeDataIntoQueryString round-trips existing search params through qs with
// parseArrays:false, which silently mangles `key[]=v` into `key[0]=v` whenever
// any sibling param changes. that breaks reads (`getAll("key[]")` returns []) and
// makes Rails see a hash instead of an array — so other filters appear to reset
// when you change one. building the URL manually preserves the array form.
function visitFilterUrl(newUrl: string, reloadProp?: string | false | string[]) {
  if (reloadProp === false) {
    router.replace({ url: newUrl, preserveState: true, preserveScroll: true });
  } else {
    const only = Array.isArray(reloadProp) ? reloadProp : reloadProp ? [reloadProp] : undefined;
    router.visit(newUrl, {
      method: "get",
      replace: true,
      preserveState: true,
      preserveScroll: true,
      ...(only ? { only } : {}),
    });
  }
}

function getQueryParamWithoutBrackets(queryParam: string) {
  return queryParam.replace(/\[\]$/, "");
}

export const FILTER_NONE_VALUE = "_none";

export function applyFilterToUrl(
  href: string,
  queryParam: string,
  value: string | string[] | undefined,
): string {
  return applyFiltersToUrl(href, { [queryParam]: value });
}

/** Apply several query-param changes at once (undefined removes a param), resetting to page 1. */
export function applyFiltersToUrl(
  href: string,
  changes: Record<string, string | string[] | undefined>,
): string {
  const url = new URL(href);
  const params = url.searchParams;

  for (const [queryParam, value] of Object.entries(changes)) {
    if (queryParam.endsWith("[]")) {
      const bracketless = getQueryParamWithoutBrackets(queryParam);
      clearMultiValueParam(params, bracketless);

      if (value === FILTER_NONE_VALUE) {
        applyMulti(url, bracketless, []);
      } else if (Array.isArray(value)) {
        applyMulti(url, bracketless, value);
      }
      // value === undefined → leave cleared (filter removed)
    } else {
      if (value === undefined) {
        params.delete(queryParam);
      } else if (Array.isArray(value)) {
        params.delete(queryParam);
        for (const v of value) params.append(queryParam, v);
      } else {
        params.set(queryParam, value);
      }
    }
  }

  params.delete("page");

  // Keep brackets unencoded so URLs match Inertia's normal style (?foo[]=bar, not ?foo%5B%5D=bar).
  const qsString = params.toString().replace(/%5B/gi, "[").replace(/%5D/gi, "]");
  return url.pathname + (qsString ? `?${qsString}` : "") + url.hash;
}

// Clears every flavor of a multi-value param: `key`, `key[]`, and `key[N]` (the
// mangled form Inertia's parseArrays:false produces after a round-trip).
function clearMultiValueParam(params: URLSearchParams, bracketless: string) {
  params.delete(bracketless);
  params.delete(`${bracketless}[]`);
  const indexedRe = new RegExp(`^${escapeRegex(bracketless)}\\[\\d+\\]$`);
  for (const key of Array.from(params.keys())) {
    if (indexedRe.test(key)) params.delete(key);
  }
}

export function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

type ToggleProps = ComponentProps<typeof Toggle>;
export function QueryParamFilterToggle(props: {
  queryParam: string;
  reloadProp?: string | false;
  label: ToggleProps["label"];
  value: string;
  size?: ToggleProps["size"];
}) {
  const page = usePage();
  const checked = () => getSearchParams(page).get(props.queryParam) === props.value;

  return (
    <Toggle
      label={props.label}
      size={props.size}
      value={props.value}
      checked={checked()}
      onChange={(e) =>
        handleFilterQueryParamChange(
          props.queryParam,
          e.target.checked ? e.target.value : undefined,
          props.reloadProp,
        )
      }
    />
  );
}
